import { useCallback, useMemo, useRef, useState } from "react";
import {
  Bell,
  BellOff,
  CheckCheck,
  Loader2,
  Settings,
  Trash2,
} from "lucide-react";
import { isToday, isYesterday, parseISO } from "date-fns";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useUnifiedNotifications } from "@/hooks/use-unified-notifications";
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll";
import { NotificationItem } from "@/components/notifications/notification-item";
import {
  NotificationPopup,
  type NotificationPopupHandle,
} from "@/components/notifications/notification-popup";
import { NotificationPreferences } from "@/components/notifications/notification-preferences";
import { IconContainer } from "@/components/ui/icon-container";
import type {
  UnifiedNotification,
  NotificationFilter,
} from "@/lib/api/unified-notifications";

type FlatRow =
  | { kind: "header"; label: string; count: number }
  | { kind: "item"; notification: UnifiedNotification };

function buildFlatRows(notifications: UnifiedNotification[]): FlatRow[] {
  const rows: FlatRow[] = [];
  let currentLabel = "";
  let headerIndex = -1;

  for (const n of notifications) {
    const date = parseISO(n.createdAt);
    const label = isToday(date)
      ? "Today"
      : isYesterday(date)
        ? "Yesterday"
        : "Earlier";

    if (label !== currentLabel) {
      currentLabel = label;
      headerIndex = rows.length;
      rows.push({ kind: "header", label, count: 0 });
    }

    const header = rows[headerIndex]!;
    if (header.kind === "header") header.count++;
    rows.push({ kind: "item", notification: n });
  }

  return rows;
}

function EmptyState({ filter }: { filter: NotificationFilter }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-20">
      <IconContainer
        icon={filter === "unread" ? CheckCheck : BellOff}
        size="lg"
        variant="muted"
      />
      <div className="text-center">
        <p className="text-muted-foreground text-sm font-medium">
          {filter === "unread"
            ? "You're all caught up"
            : "No notifications yet"}
        </p>
        <p className="text-muted-foreground/50 mt-1 text-xs">
          {filter === "unread"
            ? "All notifications have been read"
            : "Notifications will appear here as events occur"}
        </p>
      </div>
    </div>
  );
}

const HEADER_HEIGHT = 32;
const ITEM_HEIGHT = 80;

export function NotificationBell() {
  const popupRef = useRef<NotificationPopupHandle>(null);
  const bellRef = useRef<HTMLButtonElement>(null);
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const [filter, setFilter] = useState<NotificationFilter>("all");
  const [showPreferences, setShowPreferences] = useState(false);

  const onNotification = useCallback(
    (notification: { type: string; title: string; message?: string }) => {
      popupRef.current?.addNotification(notification);
    },
    []
  );

  const {
    notifications,
    unreadCount,
    isLoading,
    markRead,
    markAllRead,
    clearAll,
    isClearingAll,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useUnifiedNotifications({ filter, onNotification });

  const flatRows = useMemo(() => buildFlatRows(notifications), [notifications]);

  const virtualizer = useVirtualizer({
    count: flatRows.length,
    getScrollElement: () => scrollEl,
    estimateSize: (index) =>
      flatRows[index]?.kind === "header" ? HEADER_HEIGHT : ITEM_HEIGHT,
    overscan: 5,
  });

  const { loadMoreRef } = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    rootMargin: "200px",
    root: scrollEl,
  });

  return (
    <>
      <Sheet>
        <SheetTrigger asChild>
          <Button
            ref={bellRef}
            variant="ghost"
            size="icon"
            className="relative"
            aria-label="Notifications"
          >
            <Bell className="size-4" />
            {unreadCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-red-500 px-1 text-[10px] font-bold text-white">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </Button>
        </SheetTrigger>
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[420px]"
        >
          {/* Header */}
          <div className="shrink-0 border-b px-5 pb-4 pt-5">
            <div className="flex items-center gap-2.5">
              <SheetTitle className="text-base">Notifications</SheetTitle>
              {unreadCount > 0 && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-red-500 px-1.5 text-[11px] font-bold text-white">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </div>

            {/* Filter pills + actions */}
            <div className="mt-3 flex items-center gap-1.5">
              {(["all", "unread"] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all duration-200 ${
                    filter === f
                      ? "bg-rose-500/10 text-rose-500"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}
                >
                  {f === "all" ? "All" : "Unread"}
                  {f === "unread" && unreadCount > 0 && (
                    <span className="ml-1.5 text-[10px] opacity-70">
                      {unreadCount}
                    </span>
                  )}
                </button>
              ))}
              <div className="flex-1" />
              <TooltipProvider delayDuration={0}>
                {unreadCount > 0 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={markAllRead}
                        aria-label="Mark all read"
                        className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 rounded-md px-2 py-1 text-xs transition-colors"
                      >
                        <CheckCheck className="size-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Mark all read</TooltipContent>
                  </Tooltip>
                )}
                {notifications.length > 0 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={clearAll}
                        disabled={isClearingAll}
                        aria-label="Clear all"
                        className="text-muted-foreground hover:text-destructive flex items-center gap-1.5 rounded-md px-2 py-1 text-xs transition-colors"
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Clear all</TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => setShowPreferences((v) => !v)}
                      aria-label="Preferences"
                      className={`text-muted-foreground hover:text-foreground flex items-center rounded-md px-2 py-1 text-xs transition-colors ${
                        showPreferences ? "text-rose-500" : ""
                      }`}
                    >
                      <Settings className="size-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Preferences</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          {/* Preferences panel */}
          <NotificationPreferences open={showPreferences} />

          {/* Notification list */}
          {isLoading ? (
            <div className="flex flex-1 items-center justify-center py-20">
              <div className="text-muted-foreground text-sm">Loading...</div>
            </div>
          ) : flatRows.length === 0 ? (
            <EmptyState filter={filter} />
          ) : (
            <div ref={setScrollEl} className="flex-1 overflow-y-auto">
              <div
                style={{
                  height: virtualizer.getTotalSize(),
                  position: "relative",
                  width: "100%",
                }}
              >
                {virtualizer.getVirtualItems().map((virtualRow) => {
                  const row = flatRows[virtualRow.index]!;
                  return (
                    <div
                      key={virtualRow.index}
                      data-index={virtualRow.index}
                      ref={virtualizer.measureElement}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                    >
                      {row.kind === "header" ? (
                        <div className="bg-background/50 flex items-center gap-2 border-b px-5 py-2 backdrop-blur-sm">
                          <span className="text-muted-foreground/50 font-mono text-[10px] font-semibold uppercase">
                            {row.label}
                          </span>
                          <div className="bg-border h-px flex-1" />
                          <span className="text-muted-foreground/30 font-mono text-[10px]">
                            {row.count}
                          </span>
                        </div>
                      ) : (
                        <NotificationItem
                          notification={row.notification}
                          onMarkRead={markRead}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Infinite scroll sentinel */}
              <div ref={loadMoreRef} className="h-px" />

              {/* Loading indicator */}
              {isFetchingNextPage && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="text-muted-foreground size-4 animate-spin" />
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
      <NotificationPopup ref={popupRef} anchorRef={bellRef} />
    </>
  );
}
